import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { logout } from '../actions/AdminAction'
import { NavMenu, UserLogoutButton } from './layout/NavMenu'
import { BrandMark, Wordmark, ByPokerLauncher, ThemeToggle } from '../ui/Brand'
import { cx } from '../ui/format'

/** Phone/tablet navigation: a slim top bar plus a slide-out drawer. */
const MobileNav = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState('')
  const [role, setRole] = useState({})

  const { userInfo } = useSelector((state) => state.adminLogin)
  const { loadingGetRole, getRoleData } = useSelector((state) => state.getRoleInfo)

  useEffect(() => {
    if (userInfo) {
      setName(userInfo.name || '')
      if (userInfo.role && !getRoleData) setRole(userInfo.role)
    }
  }, [userInfo, getRoleData])

  useEffect(() => {
    if (getRoleData) setRole(getRoleData)
  }, [getRoleData])

  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    if (!isOpen) return undefined
    const onKey = (e) => e.key === 'Escape' && setIsOpen(false)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleLogout = () => {
    dispatch(logout())
    navigate('/')
    setIsOpen(false)
  }

  if (!userInfo) return null

  return (
    <>
      <header className="sm:hidden fixed top-0 inset-x-0 z-40 h-14 flex items-center justify-between gap-2 px-3 bg-cm-surface border-b border-cm-border">
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="w-10 h-10 grid place-items-center rounded-cm text-cm-text hover:bg-cm-surface-2"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        <Link to="/dashboard" className="flex items-center gap-2" aria-label="ChipMonk home">
          <BrandMark size={28} />
          <Wordmark height={13} />
        </Link>
        <ThemeToggle />
      </header>

      {isOpen && (
        <div
          className="sm:hidden fixed inset-0 z-40 bg-cm-overlay animate-cm-fade-in"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      <div
        className={cx(
          'sm:hidden fixed top-0 left-0 z-50 h-full w-72 max-w-[85vw] flex flex-col bg-cm-surface border-r border-cm-border shadow-cm-lg transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        aria-hidden={!isOpen}
      >
        <div className="flex items-center justify-between h-14 px-4 border-b border-cm-border shrink-0">
          <span className="flex items-center gap-2">
            <BrandMark size={28} />
            <Wordmark height={13} />
          </span>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="w-9 h-9 grid place-items-center rounded-cm text-cm-muted hover:bg-cm-surface-2 hover:text-cm-text"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-3">
          {isOpen && <NavMenu role={role} loading={loadingGetRole} onNavigate={() => setIsOpen(false)} />}
        </div>

        <div className="border-t border-cm-border p-3 shrink-0">
          <UserLogoutButton name={name} roleName={role?.name} onLogout={handleLogout} />
          <div className="flex justify-center pt-3">
            <ByPokerLauncher height={20} />
          </div>
        </div>
      </div>
    </>
  )
}

export default MobileNav
